/* ===================================================================
   Linear onboarding CHUTE local markers (2026-07-09).

   The chute (routes/Welcome) is a 4-step "cattle chute": Sign up -> Connect
   Google -> Send yourself a test review request -> Into the dashboard. Its
   navigation is DERIVED from real account data (useChuteProgress), never from
   stored "clicked Next" booleans -- EXCEPT for two facts the backend exposes no
   read for. Those live here, and BOTH record a REAL, honest event (materially
   different from a fabricated "done"):

     - TEST-SENT marker: written ONLY on a server-confirmed test-send success
       (POST /requests/test-send returned sent === true). Proves the owner
       actually fired the Aha email.
     - CONNECT-SKIPPED marker: written ONLY when the owner EXPLICITLY taps "skip
       for now" on the Google step. `skipped` is DISTINCT from `done` -- a
       skipped Google connect stays resurfaceable and never counts as connected
       for the send gate; it only lets the linear flow advance past step 2.

   Same per-tenant localStorage pattern as lib/onboardingState's reactivation
   marker. Cleared on logout by clearOnboardingState() there (it wipes every
   rhody_* onboarding key), plus a dedicated clear here for completeness.
   =================================================================== */

const TEST_SENT_PREFIX = 'rhody_chute_test_sent_';
const CONNECT_SKIPPED_PREFIX = 'rhody_chute_connect_skipped_';

/* Event names dispatched on a marker write, so useChuteProgress (mounted
   elsewhere) re-derives without a reload. */
export const CHUTE_TEST_SENT_EVENT = 'rhody:chute-test-sent';
export const CHUTE_CONNECT_SKIPPED_EVENT = 'rhody:chute-connect-skipped';

function key(prefix: string, clientId: string | undefined): string | null {
  return clientId ? prefix + clientId : null;
}

function readFlag(prefix: string, clientId: string | undefined): boolean {
  const k = key(prefix, clientId);
  if (!k) return false;
  try {
    return localStorage.getItem(k) != null;
  } catch {
    return false;
  }
}

function writeFlag(
  prefix: string,
  clientId: string | undefined,
  eventName: string,
  extra: Record<string, unknown> = {},
): void {
  const k = key(prefix, clientId);
  if (!k) return;
  try {
    localStorage.setItem(k, JSON.stringify({ at: new Date().toISOString(), ...extra }));
    window.dispatchEvent(new CustomEvent(eventName));
  } catch {
    /* storage unavailable -- non-fatal */
  }
}

function clearFlag(prefix: string, clientId: string | undefined, eventName: string): void {
  const k = key(prefix, clientId);
  if (!k) return;
  try {
    localStorage.removeItem(k);
    window.dispatchEvent(new CustomEvent(eventName));
  } catch {
    /* non-fatal */
  }
}

/* ---- test-sent (chute step 3) ---- */
export function getChuteTestSent(clientId: string | undefined): boolean {
  return readFlag(TEST_SENT_PREFIX, clientId);
}
export function setChuteTestSent(clientId: string | undefined, to: string): void {
  writeFlag(TEST_SENT_PREFIX, clientId, CHUTE_TEST_SENT_EVENT, { to });
}

/* ---- connect-skipped (chute step 2, skipped != done) ---- */
export function getChuteConnectSkipped(clientId: string | undefined): boolean {
  return readFlag(CONNECT_SKIPPED_PREFIX, clientId);
}
export function setChuteConnectSkipped(clientId: string | undefined): void {
  writeFlag(CONNECT_SKIPPED_PREFIX, clientId, CHUTE_CONNECT_SKIPPED_EVENT);
}
export function clearChuteConnectSkipped(clientId: string | undefined): void {
  clearFlag(CONNECT_SKIPPED_PREFIX, clientId, CHUTE_CONNECT_SKIPPED_EVENT);
}
